// Live publications feed — merged from ORCID + PubMed, deduplicated, abstracts excluded.
// 24h localStorage cache and fallback.
import { useEffect, useState } from 'react';
import { keyPapers } from './mock';

const ORCID_ID = '0000-0001-6921-1592';
const PUBMED_TERM =
  '(Cheriyan J[Author] AND (Cambridge[Affiliation] OR GSK[Affiliation] OR Addenbrooke[Affiliation]))';
const PUBMED_RETMAX = 300;
const CACHE_KEY = `pubs_${ORCID_ID}_merged_v2`;
const CACHE_TTL_MS = 3 * 60 * 60 * 1000; // 3 hours (matches backend scheduler cadence)
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// ---------- utils ----------

function cleanText(s) {
  if (!s) return s;
  return String(s)
    .replace(/<\/?(i|em|b|strong|scp|sub|sup|jats:[^>]*)[^>]*>/gi, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&#x[0-9a-f]+;/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// Journals often arrive lowercased or ISO-abbreviated. Convert to Title Case,
// keep common short prepositions/conjunctions lowercase, and preserve well-known
// medical acronyms in uppercase.
const _JOURNAL_LOWER = new Set(['a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'in', 'of', 'on', 'or', 'the', 'to', 'via', 'with', 'de', 'la']);
const _JOURNAL_UPPER = new Set(['JAMA', 'NEJM', 'BMJ', 'JACC', 'ESC', 'ACC', 'AHA', 'PLOS', 'BMC', 'JCI', 'MRI', 'CT', 'PET', 'ERJ', 'ATVB']);
function titleCaseJournal(s) {
  if (!s) return s;
  const words = s.replace(/\s+/g, ' ').trim().split(/(\s+|-)/); // preserve hyphens/spaces
  return words
    .map((w, i) => {
      if (/^\s+$|^-$/.test(w)) return w;
      const upper = w.toUpperCase();
      if (_JOURNAL_UPPER.has(upper)) return upper;
      // Already contains a capital in an unusual position — trust it (e.g. "eLife")
      if (/[A-Z]/.test(w) && w !== upper) return w;
      const lower = w.toLowerCase();
      if (i > 0 && _JOURNAL_LOWER.has(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join('');
}

function normaliseTitle(t) {
  if (!t) return '';
  return t
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

// Publication types on ORCID / PubMed that indicate an ABSTRACT rather than a full paper.
const ABSTRACT_ORCID_TYPES = new Set([
  'conference-abstract',
  'conference-poster',
  'conference-paper',
  'poster',
  'lecture-speech',
  'other',
]);
const ABSTRACT_PUBMED_TYPES = new Set([
  'Meeting Abstract',
  'Congress',
  'Congresses',
  'Lecture',
  'Video-Audio Media',
]);
// Titles that clearly indicate an abstract even when metadata is missing.
function looksLikeAbstract(title, journal) {
  if (!title) return false;
  const t = title.toLowerCase();
  if (/\babstract\b/.test(t) && /(supplement|congress|conference|meeting|poster)/i.test(`${title} ${journal || ''}`)) {
    return true;
  }
  if (/^p\d+\b/.test(t) && /(supplement|congress|conference|meeting)/i.test(journal || '')) return true;
  return false;
}

// ---------- ORCID ----------

function parseOrcidWorks(data) {
  const items = [];
  (data.group || []).forEach((group) => {
    const ws = (group['work-summary'] || [])[0];
    if (!ws) return;
    const type = (ws.type || 'journal-article').toLowerCase();
    if (ABSTRACT_ORCID_TYPES.has(type)) return; // skip abstracts / posters
    const title = cleanText(ws.title?.title?.value);
    if (!title) return;
    const journal = titleCaseJournal(cleanText(ws['journal-title']?.value || null));
    const yearRaw = ws['publication-date']?.year?.value || null;
    const monthRaw = ws['publication-date']?.month?.value || null;
    let doi = null;
    let pmid = null;
    (ws['external-ids']?.['external-id'] || []).forEach((eid) => {
      if (eid['external-id-type'] === 'doi') doi = String(eid['external-id-value'] || '').toLowerCase();
      if (eid['external-id-type'] === 'pmid') pmid = String(eid['external-id-value'] || '');
    });
    if (looksLikeAbstract(title, journal)) return;
    items.push({
      title,
      journal,
      year: yearRaw ? parseInt(yearRaw, 10) : null,
      month: monthRaw ? parseInt(monthRaw, 10) : null,
      doi,
      pmid,
      type: 'journal-article',
      source: 'orcid',
    });
  });
  return items;
}

async function fetchOrcid() {
  const res = await fetch(`https://pub.orcid.org/v3.0/${ORCID_ID}/works`, {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`ORCID ${res.status}`);
  const data = await res.json();
  return parseOrcidWorks(data);
}

// ---------- PubMed (NCBI E-utilities) ----------

function sleep(ms) { return new Promise((r) => setTimeout(r, ms)); }

async function fetchPubMedOnce() {
  const esearchUrl = `https://eutils.ncbi.nlm.nih.gov/entrez/eutils/esearch.fcgi?db=pubmed&term=${encodeURIComponent(
    PUBMED_TERM
  )}&retmax=${PUBMED_RETMAX}&retmode=json&sort=pub+date`;
  const searchRes = await fetch(esearchUrl);
  if (!searchRes.ok) throw new Error(`PubMed esearch ${searchRes.status}`);
  const searchData = await searchRes.json();
  const ids = searchData?.esearchresult?.idlist || [];
  if (ids.length === 0) return [];

  const esumUrl = `https://eutils.ncbi.nlm.nih.gov/entrez/eutils/esummary.fcgi?db=pubmed&id=${ids.join(
    ','
  )}&retmode=json`;
  const sumRes = await fetch(esumUrl);
  if (!sumRes.ok) throw new Error(`PubMed esummary ${sumRes.status}`);
  const sumData = await sumRes.json();
  const items = [];
  ids.forEach((pmid) => {
    const rec = sumData.result?.[pmid];
    if (!rec) return;
    const pubTypes = new Set((rec.pubtype || []).map((t) => String(t)));
    // Skip pure abstracts / meeting-only reports; keep anything tagged as Journal Article.
    if ([...pubTypes].every((t) => ABSTRACT_PUBMED_TYPES.has(t))) return;
    if (!pubTypes.has('Journal Article') && !pubTypes.has('Review')) return;
    const title = cleanText(rec.title || '');
    if (!title) return;
    const journal = titleCaseJournal(cleanText(rec.fulljournalname || rec.source || null));
    let year = null;
    const pubdate = rec.pubdate || rec.epubdate || '';
    const ymatch = String(pubdate).match(/^(\d{4})/);
    if (ymatch) year = parseInt(ymatch[1], 10);
    let doi = null;
    (rec.articleids || []).forEach((a) => {
      if (a.idtype === 'doi') doi = String(a.value || '').toLowerCase();
    });
    if (looksLikeAbstract(title, journal)) return;
    items.push({
      title,
      journal,
      year,
      month: null,
      doi,
      pmid,
      type: 'journal-article',
      source: 'pubmed',
    });
  });
  return items;
}

// Retry PubMed a couple of times with backoff — direct browser calls to
// eutils.ncbi.nlm.nih.gov are prone to transient CORS/rate-limit failures.
async function fetchPubMed() {
  const delays = [0, 700, 2200];
  let lastErr = null;
  for (let attempt = 0; attempt < delays.length; attempt += 1) {
    if (delays[attempt] > 0) await sleep(delays[attempt]);
    try {
      const items = await fetchPubMedOnce();
      if (items.length > 0) return items;
      // Zero results is highly suspicious for a productive author — retry.
      lastErr = new Error('PubMed returned 0 items');
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error('PubMed unavailable');
}

// ---------- Merge + dedupe ----------

function mergeItems(a, b) {
  const merged = [];
  const byDoi = new Map();
  const byPmid = new Map();
  const byTitle = new Map();

  function addOrMerge(item) {
    const doi = item.doi ? item.doi.toLowerCase() : null;
    const pmid = item.pmid ? String(item.pmid) : null;
    const tkey = normaliseTitle(item.title);
    let idx = -1;
    if (doi && byDoi.has(doi)) idx = byDoi.get(doi);
    else if (pmid && byPmid.has(pmid)) idx = byPmid.get(pmid);
    else if (tkey && byTitle.has(tkey)) idx = byTitle.get(tkey);
    if (idx >= 0) {
      // Merge — prefer richer metadata.
      const existing = merged[idx];
      merged[idx] = {
        ...existing,
        doi: existing.doi || item.doi,
        pmid: existing.pmid || item.pmid,
        journal: existing.journal || item.journal,
        year: existing.year || item.year,
        month: existing.month || item.month,
        source:
          existing.source === item.source
            ? existing.source
            : `${existing.source}+${item.source}`.split('+').sort().join('+'),
      };
      if (merged[idx].doi) byDoi.set(merged[idx].doi.toLowerCase(), idx);
      if (merged[idx].pmid) byPmid.set(String(merged[idx].pmid), idx);
      if (merged[idx].title) byTitle.set(normaliseTitle(merged[idx].title), idx);
    } else {
      merged.push(item);
      const i = merged.length - 1;
      if (doi) byDoi.set(doi, i);
      if (pmid) byPmid.set(pmid, i);
      if (tkey) byTitle.set(tkey, i);
    }
  }
  a.forEach(addOrMerge);
  b.forEach(addOrMerge);

  // Attach display URL.
  merged.forEach((it) => {
    it.url = it.doi
      ? `https://doi.org/${it.doi}`
      : it.pmid
      ? `https://pubmed.ncbi.nlm.nih.gov/${it.pmid}/`
      : null;
  });

  merged.sort((x, y) => {
    const yy = y.year || 0;
    const yx = x.year || 0;
    if (yy !== yx) return yy - yx;
    return (y.month || 0) - (x.month || 0);
  });
  return merged;
}

// ---------- Cache ----------

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (err) {
    // localStorage unavailable (private-browsing mode) or JSON corrupt.
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('[publications] readCache failed:', err);
    }
    return null;
  }
}

function writeCache(items) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), items }));
  } catch (err) {
    // Expected when the browser is in private mode or quota is exceeded.
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('[publications] writeCache failed (quota or unavailable):', err);
    }
  }
}

// ---------- Session-shared "best known" store ----------
// Module-level singleton that keeps the largest merged result seen during
// the current SPA session. Any component using usePublications() gets the
// same items array — so counts on Home and PublicationsPage always match.
// Subscribers are notified when the store changes.

const bestKnown = {
  items: null,
  at: 0,
  source: null,
};
const subscribers = new Set();

// Hydrate synchronously at module load from the localStorage cache. This
// ensures the very first render of any page has access to the historical
// best-known count — even if the initial fetch of the current session ends
// up partial (ORCID-only) due to PubMed CORS/rate-limit failure.
(function hydrateFromCache() {
  try {
    const cache = readCache();
    if (cache && Array.isArray(cache.items) && cache.items.length > 0) {
      bestKnown.items = cache.items;
      bestKnown.at = cache.at || 0;
      bestKnown.source = 'cached';
    }
  } catch (err) {
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('[publications] hydrateFromCache failed:', err);
    }
  }
})();

function notifySubscribers() {
  subscribers.forEach((cb) => {
    try { cb(); } catch { /* noop */ }
  });
}

function updateBestKnown(nextItems, nextSource, nextAt) {
  if (!Array.isArray(nextItems)) return false;
  if (!bestKnown.items || nextItems.length > bestKnown.items.length) {
    bestKnown.items = nextItems;
    bestKnown.source = nextSource;
    bestKnown.at = nextAt || Date.now();
    notifySubscribers();
    return true;
  }
  return false;
}

// ---------- Hook ----------

export function usePublications() {
  const [state, setState] = useState(() => {
    if (bestKnown.items) {
      return {
        items: bestKnown.items,
        loading: false,
        source: bestKnown.source || 'session',
        fetchedAt: bestKnown.at,
        error: null,
      };
    }
    return { items: [], loading: true, source: null, fetchedAt: null, error: null };
  });

  useEffect(() => {
    let cancelled = false;

    // Subscribe to session-shared updates so other page components stay in sync.
    const onUpdate = () => {
      if (cancelled) return;
      setState((prev) => {
        if (bestKnown.items && bestKnown.items.length > (prev.items?.length || 0)) {
          return {
            items: bestKnown.items,
            loading: false,
            source: bestKnown.source || 'session',
            fetchedAt: bestKnown.at,
            error: null,
          };
        }
        return prev;
      });
    };
    subscribers.add(onUpdate);

    async function load() {
      // 1) Try backend server cache first (populated by the 3-hour scheduler
      //    on the FastAPI backend). This gives an instant, always-fresh first
      //    paint that reflects a real recent sync, even if the browser's
      //    localStorage cache is stale or empty.
      try {
        if (API_BASE) {
          const r = await fetch(`${API_BASE}/api/publications-cache`, { cache: 'no-store' });
          if (r.ok) {
            const payload = await r.json();
            const serverItems = Array.isArray(payload.items) ? payload.items : [];
            if (serverItems.length > 0) {
              // Ensure each item has a display URL (backend attaches it, but be safe)
              serverItems.forEach((it) => {
                if (!it.url) {
                  it.url = it.doi
                    ? `https://doi.org/${it.doi}`
                    : it.pmid
                    ? `https://pubmed.ncbi.nlm.nih.gov/${it.pmid}/`
                    : null;
                }
              });
              const serverAt = payload.updated_at ? Date.parse(payload.updated_at) : Date.now();
              updateBestKnown(serverItems, 'server-cache', serverAt);
              writeCache(bestKnown.items || serverItems);
              if (!cancelled) {
                setState({
                  items: bestKnown.items || serverItems,
                  loading: false,
                  source: 'server-cache',
                  fetchedAt: serverAt,
                  error: null,
                });
              }
              // Return early — the server cache is authoritative. Client-side
              // live fetch is only used as a fallback below when this fails.
              return;
            }
          }
        }
      } catch (err) {
        // Server cache unreachable — quietly fall through to legacy client-side
        // ORCID + PubMed fetch below. Log at debug level so production noise
        // stays low but debugging is still possible.
        if (typeof console !== 'undefined' && console.debug) {
          console.debug('[publications] server-cache fetch failed, falling back to client fetch:', err);
        }
      }

      const cache = readCache();
      // Seed the session-shared store from cache if it's healthier than what we have.
      if (cache && Array.isArray(cache.items)) {
        updateBestKnown(cache.items, 'cached', cache.at);
      }
      if (cache && Date.now() - cache.at < CACHE_TTL_MS) {
        if (!cancelled) {
          setState({
            items: bestKnown.items || cache.items,
            loading: false,
            source: 'merged',
            fetchedAt: bestKnown.at || cache.at,
            error: null,
          });
        }
        return;
      }

      const results = await Promise.allSettled([fetchOrcid(), fetchPubMed()]);
      const orcidItems = results[0].status === 'fulfilled' ? results[0].value : [];
      const pubmedItems = results[1].status === 'fulfilled' ? results[1].value : [];
      const orcidErr = results[0].status === 'rejected' ? results[0].reason?.message : null;
      const pubmedErr = results[1].status === 'rejected' ? results[1].reason?.message : null;

      if (orcidItems.length === 0 && pubmedItems.length === 0) {
        if (cache) {
          if (!cancelled) {
            setState({
              items: bestKnown.items || cache.items,
              loading: false,
              source: 'cached',
              fetchedAt: bestKnown.at || cache.at,
              error: `${orcidErr || ''} ${pubmedErr || ''}`.trim(),
            });
          }
          return;
        }
        const fallback = (keyPapers || []).map((s) => ({
          title: s,
          journal: null,
          year: null,
          month: null,
          doi: null,
          pmid: null,
          url: null,
          type: 'journal-article',
          source: 'fallback',
        }));
        if (!cancelled) {
          setState({
            items: fallback,
            loading: false,
            source: 'fallback',
            fetchedAt: null,
            error: `${orcidErr || ''} ${pubmedErr || ''}`.trim(),
          });
        }
        return;
      }

      const merged = mergeItems(orcidItems, pubmedItems);
      const bothSucceeded = !orcidErr && !pubmedErr;

      // Feed the session-shared store. It internally keeps the LARGEST result
      // seen. Then persist that largest known set to localStorage so it
      // survives across sessions and page reloads.
      const improved = updateBestKnown(
        merged,
        bothSucceeded ? 'merged' : (pubmedErr ? 'orcid-only' : 'pubmed-only'),
        Date.now(),
      );
      if (improved && bestKnown.items) {
        writeCache(bestKnown.items);
      }

      const finalItems = bestKnown.items || merged;
      const finalAt = bestKnown.at || Date.now();
      const finalSource = bestKnown.items && bestKnown.items.length > merged.length
        ? (bestKnown.source || 'session')
        : (bothSucceeded ? 'merged' : (pubmedErr ? 'orcid-only' : 'pubmed-only'));

      if (!cancelled) {
        setState({
          items: finalItems,
          loading: false,
          source: finalSource,
          fetchedAt: finalAt,
          error: bothSucceeded ? null : `${orcidErr || ''} ${pubmedErr || ''}`.trim(),
        });
      }
    }

    load();
    return () => {
      cancelled = true;
      subscribers.delete(onUpdate);
    };
  }, []);

  return state;
}

export function groupByYear(items) {
  const groups = new Map();
  items.forEach((it) => {
    const key = it.year || 'Unknown';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(it);
  });
  return Array.from(groups.entries()).sort((a, b) => {
    const ay = a[0] === 'Unknown' ? -1 : a[0];
    const by = b[0] === 'Unknown' ? -1 : b[0];
    return by - ay;
  });
}
